import { useEffect, useState } from "react";
import { Button, Input, type InputProps } from "../ui";

const configuredSecretPlaceholder = "••••••••";
/**
 * Fremde Zugangsdaten (Bank-PIN, Dateipasswort, Anbieterschlüssel): natives Passwortfeld, damit der Wert nie im
 * Formularverlauf landet, und `one-time-code`, damit Chrome es nicht als eigenes Passwort zum Speichern anbietet
 * (Chromium `form_data_parser.cc`, `IsNotPasswordField`). `new-password`/`off` lösen das Speichern-Angebot aus.
 */
const foreignSecretAutoComplete = "one-time-code";

export type SecretValueInputProps = Omit<InputProps, "type"> & {
  visibilityToggle?: boolean; showText: string; hideText: string;
};

/** Unconfigured foreign credential value; only the login dialog keeps the browser's own password handling. */
export function SecretValueInput({ visibilityToggle = false, showText, hideText, suffix, style, ...props }: SecretValueInputProps) {
  return <Input.Password {...props} visibilityToggle={visibilityToggle} visibilityLabels={{ show: showText, hide: hideText }} autoComplete={foreignSecretAutoComplete} spellCheck={false} autoCapitalize="none" autoCorrect="off"
    data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other" data-mqorva-no-browser-autofill="true"
    style={style}
    suffix={suffix} />;
}

type SecretInputProps = {
  value?: string;
  onChange?: (event: any) => void;
  configured: boolean;
  configuredText: string;
  replaceText: string;
  showText: string;
  hideText: string;
  placeholder?: string;
  id?: string;
  name?: string;
  onReplace?: () => void;
  onCancelReplace?: () => void;
};

type ControlledSecretInputProps = Omit<SecretInputProps, "replaceText" | "onReplace"> & {
  replacing: boolean;
  /** Start editing a stored value directly in the existing field. */
  onStartReplace?: () => void;
};

export function SecretInput({
  value,
  onChange,
  configured,
  configuredText,
  replaceText,
  showText,
  hideText,
  placeholder,
  id,
  name,
  onReplace,
  onCancelReplace,
}: SecretInputProps) {
  const [replacing, setReplacing] = useState(!configured);

  useEffect(() => {
    setReplacing(!configured);
  }, [configured]);

  if (configured && !replacing) {
    return (
      <Input
        value={configuredSecretPlaceholder}
        readOnly
        aria-label={configuredText}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        id={id}
        name={name}
        suffix={
          <Button
            type="link"
            size="small"
            onClick={() => {
              setReplacing(true);
              onChange?.({ target: { value: "" } });
              return onReplace?.();
            }}
          >
            {replaceText}
          </Button>
        }
      />
    );
  }

  return (
    <Input.Password
      visibilityLabels={{ show: showText, hide: hideText }}
      value={value}
      onChange={onChange}
      onBlur={() => {
        if (configured && !value) {
          setReplacing(false);
          onCancelReplace?.();
        }
      }}
      placeholder={placeholder}
      autoComplete={foreignSecretAutoComplete}
      spellCheck={false}
      autoCapitalize="none"
      autoCorrect="off"
      data-lpignore="true"
      data-1p-ignore="true"
      data-bwignore="true"
      id={id}
      name={name}
    />
  );
}

export function ControlledSecretInput({
  value,
  onChange,
  configured,
  configuredText,
  showText,
  hideText,
  replacing,
  placeholder,
  id,
  name,
  onCancelReplace,
  onStartReplace,
}: ControlledSecretInputProps) {

  if (configured && !replacing) {
    return (
      <Input
        value={configuredSecretPlaceholder}
        readOnly
        onFocus={onStartReplace}
        suffix={onStartReplace ? <span /> : undefined}
        aria-label={configuredText}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        id={id}
        name={name}
      />
    );
  }

  return (
    <Input.Password
      visibilityToggle={!onStartReplace || !!value}
      visibilityLabels={{ show: showText, hide: hideText }}
      value={value}
      onChange={onChange}
      onBlur={() => {
        if (configured && !value) {
          onCancelReplace?.();
        }
      }}
      placeholder={placeholder}
      autoComplete={foreignSecretAutoComplete}
      spellCheck={false}
      autoCapitalize="none"
      autoCorrect="off"
      data-lpignore="true"
      data-1p-ignore="true"
      data-bwignore="true"
      id={id}
      name={name}
    />
  );
}
